import type { GeoPoint } from '../model/order-schema';

/**
 * Map geometry + staleness helpers, ported from cpanel's order-single map.
 * ymaps3 expects coordinates as `[long, lat]`; the API gives `{lat, long}` — we
 * flip at this single boundary (cpanel flips at every map component; we
 * centralize it). Staleness color thresholds match cpanel `getStatusColor`.
 */

/** API `{lat, long}` → ymaps3 `[long, lat]`. */
export function toLngLat(point: GeoPoint | null | undefined): [number, number] | null {
  if (!point || !Number.isFinite(point.lat) || !Number.isFinite(point.long)) return null;
  return [point.long, point.lat];
}

const STALE_RED_MINUTES = 10;
const STALE_YELLOW_MINUTES = 5;
const MS_PER_MINUTE = 60_000;

export type LocationFreshness = 'fresh' | 'stale' | 'expired';

/**
 * Classify a courier location's freshness from its `last_reported_at`.
 * >10 min → expired (red), >5 min → stale (yellow), else fresh (green).
 * Matches cpanel `getStatusColor`.
 */
export function getLocationFreshness(
  lastReportedAt: string | null | undefined,
  now: number = Date.now(),
): LocationFreshness {
  if (!lastReportedAt) return 'expired';
  const diffMinutes = (now - new Date(lastReportedAt).getTime()) / MS_PER_MINUTE;
  if (diffMinutes > STALE_RED_MINUTES) return 'expired';
  if (diffMinutes > STALE_YELLOW_MINUTES) return 'stale';
  return 'fresh';
}

/** Hex color for a freshness level (brand-aligned green/amber/red). */
export function freshnessColor(freshness: LocationFreshness): string {
  switch (freshness) {
    case 'fresh':
      return '#22C55E';
    case 'stale':
      return '#EAB308';
    case 'expired':
      return '#EF4444';
  }
}

/**
 * Minutes-ago label fragment for a marker hint. Returns a number of whole
 * minutes since `lastReportedAt` (the UI formats with i18n pluralization).
 */
export function minutesAgo(
  lastReportedAt: string | null | undefined,
  now: number = Date.now(),
): number | null {
  if (!lastReportedAt) return null;
  return Math.max(
    0,
    Math.floor((now - new Date(lastReportedAt).getTime()) / MS_PER_MINUTE),
  );
}
